// Account page, under the e-mail: how much of the account is in use. Saved
// projects and today's simulations (and market optimisations, where the
// account has them), each against its limit when there is one. Shown to every
// account that is not an admin, demo or regular, in the selected language.
// The numbers come from /profiles/me/demo (DemoContext) and are refreshed
// when the page opens.
import { useEffect } from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { useDemo } from "../context/DemoContext";
import { useLanguage } from "../context/LanguageContext";
import { accountUsage } from "../utils/demoAccess";

const hasLimit = (item) => item && item.limit !== null && item.limit !== undefined;

export function usageRow(item, kind, t) {
  if (!hasLimit(item)) {
    return { value: t("usage.panel.noLimit", { used: item.used }), note: null, percent: null, full: false };
  }
  const left = Math.max(0, item.limit - item.used);
  const full = item.used >= item.limit;
  let note;
  if (full) note = t("usage.panel.full");
  else if (kind === "projects") note = t("usage.panel.projectsLeft", { count: left });
  else note = left === 1 ? t("usage.panel.runsLeftOne") : t("usage.panel.runsLeft", { count: left });
  const percent = item.limit > 0 ? Math.min(100, (item.used / item.limit) * 100) : 100;
  return { value: t("usage.panel.ofLimit", { used: item.used, limit: item.limit }), note, percent, full };
}

function UsageRow({ Icon, label, hint, item, kind, t }) {
  const row = usageRow(item, kind, t);
  const color = row.full ? "error" : row.percent !== null && row.percent >= 80 ? "warning" : "primary";
  return (
    <Box data-testid={`usage-${kind}`}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
          {row.value}
        </Typography>
      </Stack>
      {row.percent !== null ? (
        <LinearProgress
          variant="determinate"
          value={row.percent}
          color={color}
          aria-label={label}
          sx={{ mt: 0.75, height: 8, borderRadius: 4 }}
        />
      ) : null}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={{ xs: 0.25, sm: 2 }} sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
        {row.note ? (
          <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
            color={row.full ? "error.main" : "text.secondary"}>
            {row.note}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function AccountUsagePanel() {
  const { demoProfile, refreshDemoProfile } = useDemo();
  const { t } = useLanguage();

  useEffect(() => {
    refreshDemoProfile?.({ quiet: true });
    // Once per visit to the account page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usage = accountUsage(demoProfile);
  if (!usage) return null;

  return (
    <Box
      data-testid="account-usage-panel"
      data-no-translate=""
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        p: 2,
        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f8fbff"),
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main", letterSpacing: "0.03em" }}>
          {t("usage.title")}
        </Typography>
        <Typography variant="caption" color="text.secondary">{t("usage.panel.subtitle")}</Typography>
      </Box>
      {usage.projects ? (
        <UsageRow Icon={FolderOutlinedIcon} kind="projects" item={usage.projects} t={t}
          label={t("usage.panel.projects")} hint={t("usage.panel.projectsHint")} />
      ) : null}
      <UsageRow Icon={PlayCircleOutlineIcon} kind="simulations" item={usage.simulations} t={t}
        label={t("usage.panel.simulations")} hint={t("usage.panel.simulationsHint")} />
      {usage.optimizations ? (
        <UsageRow Icon={ShowChartIcon} kind="optimizations" item={usage.optimizations} t={t}
          label={t("usage.panel.optimizations")} hint={t("usage.panel.optimizationsHint")} />
      ) : null}
    </Box>
  );
}
